import * as React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    WithStyles
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./EmptyOrderView.styles";
import { adminFeeDefault, adminFeeLimit, deliveryMethod, emptyOrderStats, orderTypes } from "../../../constants/ORDER_DETAILS";
import { ccyFormat, formatPrice } from "../../../utilities/PriceUtils";
import { useTranslation } from "react-i18next";
import { isPriceValid } from "../../../utilities/generalUtils";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { useDispatch, useSelector } from "react-redux";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import { AddressCard } from "../../components/OrderDetails/AddressCard";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useAuthContext } from "../../../auth/AuthProvider";
import * as QuotaAction from "../../../stores/quotas/QuotaAction";
import { isAddressExist } from "../../../utilities/userUtils";
import { isEuCountry } from "../../../utilities/countriesUtils";
import { ProductsTable } from "../../components/OrderDetails/ProductsTable";
import { AutocompleteUserDropdown } from "../../components/FormComponents/AutocompleteUserDropdown";
import { EmptyOrderFilesComponent } from "../../components/EmptyOrderFiles/EmptyOrderFilesComponent";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface IEmptyOrderComponentProps extends WithStyles<typeof styles> {
    order: any,
    createOrder: any,
    allUsers: any,
    onBehalf: boolean,
    items?: any
}

const EmptyOrderComponentPure: React.FunctionComponent<IEmptyOrderComponentProps> = ({
                                                                                         classes,
                                                                                         order,
                                                                                         createOrder,
                                                                                         allUsers,
                                                                                         onBehalf,
                                                                                         items
                                                                                     }: IEmptyOrderComponentProps) => {

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const authContext = useAuthContext();
    const adminUserId = authContext?.user?.id;
    const countries = useSelector(state => selectVisibleCountries(state));

    let deliveryCountryFilled = false;
    let vatIdFilled = false;
    let countryVat = 0;

    const [orderDetails, setOrderDetails] = React.useState({
        userId: order.userId,
        orderState: order.orderState,
        type: order.type,
        adminFee: order.adminFee,
        discount: order.discount,
        vat: countryVat,
        totalPrice: order.totalPrice,
        remark: order.remark,
        paymentMethod: "",
        emptyOrderFiles: []
    });

    const [detailsChanged, setDetailsChanged] = React.useState(false);
    const [paymentMethodSelected, setPaymentMethodSelected] = React.useState(false);

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setOrderDetails({
            ...orderDetails,
            [name]: value
        });
        if (name === "userId") {
            setDetailsChanged(true);
        }
        if (name === "paymentMethod") {
            setPaymentMethodSelected(true);
            if (value === "Quota") {
                setOrderDetails({
                    ...orderDetails,
                    [name]: value,
                    orderState: "Registered"
                });
            }
        }
    };

    const handleUserChange = user => {
        setOrderDetails({
            ...orderDetails,
            userId: user?.userId
        });
        setDetailsChanged(true);
    };

    React.useEffect(() => {
        dispatch(QuotaAction.requestUserQuota(adminUserId));
        if (orderDetails.userId) {
            dispatch(UserDetailsAction.requestUser(orderDetails.userId));
        }
    }, [dispatch, orderDetails.userId, adminUserId]);

    const userDetails = useSelector((state) => state.userDetails?.requestedUser);
    const userQuota = useSelector(state => state.quotas?.userQuota);

    const handleDiscount = (event) => {
        if (Number(subtotalPrice) > 0) {
            if ((isPriceValid(event.target.value) && Number(event.target.value) <= Number(subtotalPrice)) || event.target.value === "") {
                setOrderDetails({
                    ...orderDetails,
                    [event.target.name]: event.target.value
                });
                if (orderDetails.userId) {
                    setDetailsChanged(true);
                }

                let currentDiscount = parseFloat(event.target.value).toFixed(2);
                if (Number(parseFloat(subtotalPrice.toString()).toFixed(2)) - Number(currentDiscount) === 0) {
                    setOrderDetails({
                        ...orderDetails,
                        discount: Number(event.target.value),
                        vat: 0,
                        adminFee: 0,
                        totalPrice: 0
                    });
                }
            }
        } else {
            if ((isPriceValid(event.target.value) && Number(event.target.value) <= Number(orderDetails.adminFee)) || event.target.value === "") {
                setOrderDetails({
                    ...orderDetails,
                    [event.target.name]: event.target.value
                });
                if (orderDetails.userId) {
                    setDetailsChanged(true);
                }

                let currentDiscount = parseFloat(event.target.value).toFixed(2);
                if (Number(parseFloat(subtotalPrice.toString()).toFixed(2)) - Number(currentDiscount) === 0) {
                    setOrderDetails({
                        ...orderDetails,
                        discount: Number(event.target.value),
                        vat: 0,
                        totalPrice: 0
                    });
                }
            }
        }
    };

    const localCreateOrder = (event) => {
        event.preventDefault();
        const sendDetails = {
            userId: orderDetails.userId,
            orderState: orderDetails.paymentMethod === "Quota" ? "Registered" : orderDetails.orderState,
            orderType: orderDetails.type,
            adminFee: items ? adminFee : orderDetails.adminFee,
            discount: orderDetails.discount,
            vat: countryVat,
            remark: orderDetails.remark,
            totalPrice: orderDetails.totalPrice > 0 ? orderDetails.totalPrice : 0,
            paymentMethod: orderDetails.paymentMethod,
            quotaId: orderDetails.paymentMethod === "Quota" ? userQuota.id : "",
            s3FilePath: orderDetails.emptyOrderFiles
        };

        createOrder(sendDetails);

        setDetailsChanged(false);
    };

    if (orderDetails.userId) {
        if (userDetails && isAddressExist(userDetails.deliveryAddress)) {
            deliveryCountryFilled = true;
            if (isEuCountry(countries, userDetails.deliveryAddress.country)) {
                vatIdFilled = userDetails.tin !== "";
                if (userDetails.deliveryAddress.country === "Deutschland (Germany)") {
                    countryVat = 19;
                } else {
                    countryVat = 0;
                }
            } else {
                vatIdFilled = true;
                countryVat = 0;
            }

        }
    }

    let subtotalPrice = 0.0;
    if (onBehalf) {
        for (let item of items) {
            subtotalPrice += item.price;
        }
    }

    let adminFee = orderDetails.adminFee;
    if (subtotalPrice <= adminFeeLimit) {
        adminFee = adminFeeDefault;
    }

    let subtotalPriceNoVat = 0.00;
    if (items === undefined) {
        subtotalPriceNoVat = subtotalPrice + Number(orderDetails.adminFee) - Number(orderDetails.discount);
    } else {
        subtotalPriceNoVat = subtotalPrice + Number(adminFee) - Number(orderDetails.discount);
    }

    let vatValue = (subtotalPriceNoVat * Number(countryVat)) / 100;
    if (orderDetails.paymentMethod === "Quota") {
        vatValue = 0.00;
    }
    let totalPriceFrontend = subtotalPriceNoVat + vatValue;

    const handleEmptyOrderFiles = paths => {
      setOrderDetails({
          ...orderDetails,
          emptyOrderFiles: paths
      })
    };

    return <div className={classes.bottomMargin}>

        {onBehalf
            ? <div>
                <Typography variant="h2">Create new order on behalf</Typography>
            </div>
            :
            <div className={classes.title}>
                <Typography variant="h2">{t("admin.manageOrdersView.createEmptyOrder")}</Typography>
            </div>
        }

        <Grid className={classes.bottomMargin} container spacing={2} direction="row" alignItems="center">
            <Grid item xs={4}>
            <Typography variant="h4">Select user from the list</Typography>
                <AutocompleteUserDropdown
                    allUsers={allUsers}
                    selectedUserId={orderDetails.userId}
                    handleUserChange={handleUserChange}
                />
            </Grid>

            <Grid item xs={4}>
                <Typography variant="h4">{t("orderDetails.orderStatus")}</Typography>
                <FormControl required variant="outlined" className={classes.formControl}>
                    <Select
                        labelId="orderState"
                        id="orderState"
                        name="orderState"
                        value={orderDetails.orderState}
                        onChange={handleChange}
                    >
                        {orderDetails.paymentMethod === "Quota"
                            ? <MenuItem key={"Registered"} value={"Registered"}>Registered</MenuItem>
                            : emptyOrderStats.map(os => (
                                <MenuItem key={os} value={os}>{os}</MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={4}>
                <Typography variant="h4">{t("orderDetails.orderType")}</Typography>
                <FormControl required variant="outlined" className={classes.formControl}>
                    <Select
                        labelId="orderType"
                        id="ordertype"
                        name="type"
                        value={orderDetails.type}
                        onChange={handleChange}
                    >
                        {orderTypes.map(ot => (
                            <MenuItem key={ot} value={ot}>{ot}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>

        {orderDetails.userId && userDetails && <div>
            <Grid container spacing={5} direction="row">
                <Grid item xs={6}>
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography variant="h6" className={classes.cardTitle}>User's information</Typography>
                            <Typography variant="body2">Email: {userDetails.email}</Typography>
                            <Typography variant="body2">Phone number: {userDetails.phone}</Typography>
                            {userDetails.tin !== ""
                                ? <Typography variant="body2">VAT ID: {userDetails.tin}</Typography>
                                : userDetails.deliveryAddress.country && isEuCountry(countries, userDetails.deliveryAddress.country) &&
                                <Typography variant="body2" className={classes.paymentError}>VAT ID is mandatory for EU
                                    residents!</Typography>
                            }
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={3}>
                    <AddressCard float address={userDetails.billingAddress} title={t("orderDetails.title.billingAddress")} />
                </Grid>
                <Grid item xs={3}>
                    <AddressCard float address={userDetails.deliveryAddress} title={t("general.deliveryAddress")} />
                </Grid>
            </Grid>

        </div>}

        {onBehalf
            ? <div>
                <Typography variant="h4">{t("orderDetails.title.orderDetails")}</Typography>
                <ProductsTable tableItems={items} />
            </div>
            : null
        }

        <Grid className={classes.bottomMargin} container spacing={3} direction="row">
            <Grid item xs={12} sm={6}>
                <Typography variant="h4">{t("orderDetails.orderComments")}</Typography>
                <Typography variant="body1">{t("orderDetails.updateOrderComments")}</Typography>
                <TextField
                    className={classes.comment}
                    variant="outlined"
                    name="remark"
                    type="text"
                    id="remark"
                    multiline
                    rows={8}
                    value={orderDetails.remark}
                    onChange={handleChange}
                />
            </Grid>


            <Grid item xs={12} sm={6}>
                <TableContainer>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={4} align="left">{t("orderDetails.subtotal")}</TableCell>
                                <TableCell align="right">{ccyFormat(Number(subtotalPrice))}</TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell colSpan={4} align="left">{t("orderDetails.adminFee")}</TableCell>
                                {items === undefined
                                    ? <TableCell align="right">
                                        <TextField
                                            className={classes.updateFieldSmall}
                                            variant="outlined"
                                            size="small"
                                            name="adminFee"
                                            id="adminFee"
                                            type="text"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">€</InputAdornment>
                                            }}
                                            value={orderDetails.adminFee}
                                            onChange={(event) => {
                                                if (isPriceValid(event.target.value) || event.target.value === "") {
                                                    setOrderDetails({
                                                        ...orderDetails,
                                                        adminFee: Number(event.target.value)
                                                    });
                                                    if (orderDetails.userId) {
                                                        setDetailsChanged(true);
                                                    }
                                                    ;
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    : <TableCell align="right">
                                        {ccyFormat(Number(adminFee))}
                                    </TableCell>
                                }
                            </TableRow>

                            <TableRow>
                                <TableCell colSpan={4} align="left">{t("orderDetails.discount")}</TableCell>
                                <TableCell align="right">
                                    <TextField
                                        className={classes.updateFieldSmall}
                                        variant="outlined"
                                        size="small"
                                        name="discount"
                                        id="discount"
                                        type="text"
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                                        }}
                                        value={orderDetails.discount}
                                        onChange={handleDiscount}
                                    />
                                </TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell colSpan={4} align="left">{t("orderDetails.deliveryMethod")}</TableCell>
                                <TableCell
                                    align="right">{deliveryMethod}</TableCell>
                            </TableRow>

                            {userDetails && isEuCountry(countries, userDetails.deliveryAddress.country)
                                ? <>
                                    <TableRow>
                                        <TableCell colSpan={4} align="left">{t("general.totalAmountNoVat")}</TableCell>
                                        <TableCell
                                            align="right">{ccyFormat(subtotalPriceNoVat)}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell colSpan={4}
                                                align="left">{t("orderDetails.vat")} ({orderDetails.paymentMethod === "Quota" ? 0.00 : countryVat}%)*</TableCell>
                                        <TableCell align="right">
                                            {ccyFormat(vatValue)}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className={classes.bold} colSpan={4} align="left">
                                            {t("general.totalAmountVat")}
                                        </TableCell>
                                        <TableCell className={classes.bold}
                                                align="right">{ccyFormat(totalPriceFrontend)}
                                        </TableCell>
                                    </TableRow>
                                </>
                                : <>
                                    <TableRow>
                                        <TableCell className={classes.bold} colSpan={4} align="left">
                                            Total amount (net amount)*
                                        </TableCell>
                                        <TableCell className={classes.bold}
                                                align="right">{ccyFormat(totalPriceFrontend)}
                                        </TableCell>
                                    </TableRow>
                                </>
                            }
                            
                        </TableBody>
                    </Table>
                </TableContainer>
                {userDetails && isEuCountry(countries, userDetails.deliveryAddress.country)
                    ? <Typography variant="body2">{t("basket.euDirective")}</Typography>
                    : <Typography variant="body2">*please note displayed prices are net amounts without any applicable taxes</Typography>
                }
            </Grid>
        </Grid>

        {
            onBehalf
                ? null
                : <>
                    <Typography variant="h2">Add files to empty order</Typography>
                    <EmptyOrderFilesComponent onChange={handleEmptyOrderFiles} />
                </>
        }

        <Typography className={classes.topMargin} variant="h3">{t("basket.chosePayment")}</Typography>
        <FormControl style={{width: "100%"}}>
            <RadioGroup value={orderDetails.paymentMethod} onChange={event => handleChange(event)}>
                <Accordion onClick={event => handleChange(event)}>
                    <AccordionSummary id="panel1bh-header" expandIcon={<ExpandMoreIcon />}>
                        <FormControlLabel
                            name="paymentMethod"
                            value="BankTransfer"
                            control={<Radio color="primary" onClick={event => handleChange(event)} />}
                            label={t("basket.bankTransfer")}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>{t("basket.bankTransferText")}</Typography>
                    </AccordionDetails>
                </Accordion>
                {userQuota
                    ? <Accordion onClick={event => handleChange(event)}>
                        <AccordionSummary id="panel2bh-header" expandIcon={<ExpandMoreIcon />}>
                            <Tooltip
                                title={
                                    userQuota.remainingAmount < totalPriceFrontend ? "Quota has an insufficient limit" : ""
                                }
                            >
                                <FormControlLabel
                                    name="paymentMethod"
                                    value="Quota"
                                    control={<Radio color="primary" onClick={event => handleChange(event)} />}
                                    label="Quota"
                                />
                            </Tooltip>
                        </AccordionSummary>
                        <AccordionDetails>
                            <div>
                                <Typography>Only available for Eurex users.</Typography>
                                <Typography display="block">
                                    Available quota: {formatPrice(userQuota?.remainingAmount)}
                                </Typography>
                                <Typography display="block">Total quota: {formatPrice(userQuota?.totalAmount)}</Typography>
                                <Typography display="block">Used quota: {formatPrice(userQuota?.usedAmount)}</Typography>
                            </div>
                        </AccordionDetails>
                    </Accordion>
                    : null
                }
            </RadioGroup>
        </FormControl>

        <div className={classes.buttonRight}>
            <BlueButton
                disabled={!detailsChanged || !paymentMethodSelected || !deliveryCountryFilled || !vatIdFilled}
                onClick={localCreateOrder}
            >Create order
            </BlueButton>
        </div>

    </div>;

};

export const EmptyOrderComponent = withStyles(styles)(EmptyOrderComponentPure);
